import { useEffect, useMemo, useRef, useState } from 'react'
import { diffGraphs, isEmptyDiff } from './diff.js'
import type { Graph } from './graph.js'
import { formatMoment } from '../ui/moment.js'

export interface PipelineVersion {
  id: string
  number: number
  graph: unknown
  publishedAt: string
  publishedBy: string
}

export interface VersionsClient {
  pipelineVersions(
    pipelineId: string,
    page?: { limit?: number; cursor?: string },
  ): Promise<{ items: PipelineVersion[]; nextCursor: string | null }>
}

export interface VersionCompareProps {
  client: VersionsClient
  pipelineId: string
  /**
   * Черновик: его тоже сравнивают с опубликованной версией — чаще, чем две
   * старые версии между собой.
   */
  draft?: Graph | null
  /** Откатить сценарий к версии: сам откат делает вызывающий. */
  onRestore?: (version: PipelineVersion) => void
}

const DRAFT_ID = 'draft'

function graphOf(version: PipelineVersion): Graph {
  // Граф версии приходит из службы уже разобранным при публикации: здесь он
  // только читается, и падать на нём поздно — он уже опубликован.
  return (version.graph ?? { nodes: [], connections: [] }) as Graph
}

/**
 * Версии сценария и разница между двумя из них (FR-UI-007, FR-PIPE-001).
 *
 * Разница считается на клиенте: она чистая функция от двух графов, и второй
 * ответ на тот же вопрос со стороны службы завёл бы второе определение
 * слова «изменилось».
 */
export function VersionCompare({
  client,
  pipelineId,
  draft = null,
  onRestore,
}: VersionCompareProps): React.JSX.Element {
  const [versions, setVersions] = useState<readonly PipelineVersion[]>([])
  const [error, setError] = useState<string | null>(null)
  const [fromId, setFromId] = useState<string | null>(null)
  const [toId, setToId] = useState<string | null>(null)
  const current = useRef(client)
  current.current = client

  useEffect(() => {
    let cancelled = false
    current.current
      .pipelineVersions(pipelineId)
      .then((page) => {
        if (cancelled) return
        setVersions(page.items)
        setError(null)
        // Сразу выбраны две свежие: сравнивают почти всегда их, и заставлять
        // выбирать вручную то, что нужно в девяти случаях из десяти, —
        // лишняя работа.
        setFromId(page.items[1]?.id ?? page.items[0]?.id ?? null)
        setToId(draft === null ? (page.items[0]?.id ?? null) : DRAFT_ID)
      })
      .catch((failure: unknown) => {
        if (cancelled) return
        setError(failure instanceof Error ? failure.message : 'Не удалось прочитать версии.')
      })

    return () => {
      cancelled = true
    }
  }, [pipelineId, draft])

  const options = useMemo(() => {
    const list = versions.map((version) => ({
      id: version.id,
      label: `Версия ${version.number}`,
      graph: graphOf(version),
    }))
    return draft === null ? list : [{ id: DRAFT_ID, label: 'Черновик', graph: draft }, ...list]
  }, [versions, draft])

  const from = options.find((option) => option.id === fromId)
  const to = options.find((option) => option.id === toId)
  const diff = useMemo(
    () => (from === undefined || to === undefined ? null : diffGraphs(from.graph, to.graph)),
    [from, to],
  )

  return (
    <section className="версии" aria-label="Версии сценария">
      {error !== null ? (
        <p className="версии__отказ" role="alert">
          {error}
        </p>
      ) : null}

      {versions.length === 0 && error === null ? (
        <p className="версии__пусто">Сценарий ещё не публиковался. После публикации здесь появятся версии, и их можно будет сравнивать между собой и с черновиком.</p>
      ) : (
        <>
          <ul className="версии__список">
            {versions.map((version) => (
              <li key={version.id}>
                <span>{`Версия ${version.number}`}</span>
                <span className="версия__дата">{formatMoment(version.publishedAt)}</span>
                <span className="версия__автор">{version.publishedBy}</span>
                {onRestore !== undefined ? (
                  <button
                    type="button"
                    onClick={() => {
                      onRestore(version)
                    }}
                  >
                    Вернуться к этой версии
                  </button>
                ) : null}
              </li>
            ))}
          </ul>

          <label htmlFor="версия-от">Сравнить с</label>
          <select
            id="версия-от"
            value={fromId ?? ''}
            onChange={(event) => {
              setFromId(event.target.value)
            }}
          >
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>

          <label htmlFor="версия-до">Что сравниваем</label>
          <select
            id="версия-до"
            value={toId ?? ''}
            onChange={(event) => {
              setToId(event.target.value)
            }}
          >
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>

          {diff === null ? null : isEmptyDiff(diff) ? (
            <p className="различия__нет">Различий нет.</p>
          ) : (
            <dl className="различия">
              {diff.addedNodes.length > 0 ? (
                <>
                  <dt>Добавлены узлы</dt>
                  <dd>{diff.addedNodes.map((node) => node.name).join(', ')}</dd>
                </>
              ) : null}
              {diff.removedNodes.length > 0 ? (
                <>
                  <dt>Удалены узлы</dt>
                  <dd>{diff.removedNodes.map((node) => node.name).join(', ')}</dd>
                </>
              ) : null}
              {diff.changedNodes.length > 0 ? (
                <>
                  <dt>Изменены узлы</dt>
                  <dd>
                    <ul>
                      {diff.changedNodes.map((node) => (
                        // Поля перечислены: «узел изменён» без указания, что
                        // именно, не отвечает на вопрос, зачем сравнивали.
                        <li key={node.id}>{`${node.name}: ${node.fields.join(', ')}`}</li>
                      ))}
                    </ul>
                  </dd>
                </>
              ) : null}
              {diff.addedConnections.length > 0 ? (
                <>
                  <dt>Добавлены связи</dt>
                  <dd>{diff.addedConnections.join('; ')}</dd>
                </>
              ) : null}
              {diff.removedConnections.length > 0 ? (
                <>
                  <dt>Удалены связи</dt>
                  <dd>{diff.removedConnections.join('; ')}</dd>
                </>
              ) : null}
            </dl>
          )}
        </>
      )}
    </section>
  )
}
